/**
 * Pure code-snippet builder for the API Reference right pane (SP-8216).
 *
 * Ported from the design-handoff prototype's `buildSnippet`, adapted to TS and
 * to take the environment's API base URL as a parameter (so snippets show the
 * real host, not a hardcoded one). Emits cURL / Python / Node examples for an
 * endpoint, always including the two auth headers Edge's API requires.
 *
 * Auth follows the Client Integration API doc: HTTP Basic auth where
 * `$CREDENTIALS` is the base64 encoding of `login:password` (the caller's MVC
 * login/password), plus the static `api-consumer-key: client-integration`
 * header. After the first authenticated call the returned `x-auth-token` may be
 * sent instead of Basic on subsequent requests.
 */

import { RefEndpoint } from './apiReferenceModel';

export type SnippetLang = 'curl' | 'python' | 'node';

const AUTH_HEADER = 'Authorization: Basic $CREDENTIALS';
const CONSUMER_HEADER = 'api-consumer-key: client-integration';
const CONTENT_HEADER = 'Content-Type: application/json';

/**
 * Substitute `{name}` path params with their derived placeholder values. The
 * base URL already carries the `/v1` prefix (it's `${host}/v1`), so we strip a
 * leading `/v1` from the spec path to avoid a doubled segment — matching the
 * Try-It request mechanics.
 */
const resolvePath = (e: RefEndpoint): string =>
    e.path
        .replace(/^\/v1/, '')
        .replace(/\{(\w+)\}/g, (_m, name) => {
            const pp = e.params.find(p => p.name === name);
            return pp && pp.placeholder ? pp.placeholder : `{${name}}`;
        });

/** Required query params, as `name=placeholder` pairs. */
const requiredQuery = (e: RefEndpoint): Array<[string, string]> =>
    e.params
        .filter(p => p.required && p.loc === 'query')
        .map(p => [p.name, p.placeholder || '…'] as [string, string]);

const isWrite = (e: RefEndpoint): boolean =>
    e.method === 'post' || e.method === 'put' || e.method === 'patch';

const oneLineBody = (body: unknown): string =>
    body === undefined ? '{}' : JSON.stringify(body);

const indentedBody = (body: unknown): string =>
    body === undefined ? '{}' : JSON.stringify(body, null, 2);

const buildCurl = (e: RefEndpoint, base: string): string => {
    const path = resolvePath(e);
    const reqQ = requiredQuery(e);
    const qs = reqQ.map(([k, v]) => `${k}=${v}`).join('&');
    const verb = e.method.toUpperCase();

    if (isWrite(e)) {
        const url = base + path;
        return [
            `curl -X ${verb} "${url}" \\`,
            `  -H "${AUTH_HEADER}" \\`,
            `  -H "${CONSUMER_HEADER}" \\`,
            `  -H "${CONTENT_HEADER}" \\`,
            `  -d '${oneLineBody(e.requestBodyExample)}'`,
        ].join('\n');
    }

    const url = base + path + (qs ? `?${qs}` : '');
    return [
        `curl "${url}" \\`,
        `  -H "${AUTH_HEADER}" \\`,
        `  -H "${CONSUMER_HEADER}"`,
    ].join('\n');
};

const buildPython = (e: RefEndpoint, base: string): string => {
    const path = resolvePath(e);
    const reqQ = requiredQuery(e);
    const headers =
        'headers={\n' +
        `    "Authorization": "Basic $CREDENTIALS",\n` +
        `    "api-consumer-key": "client-integration",\n` +
        '}';

    if (isWrite(e)) {
        const url = base + path;
        return [
            'import requests',
            '',
            `${headers}`,
            `body = ${indentedBody(e.requestBodyExample)}`,
            `resp = requests.${e.method}("${url}", headers=headers, json=body)`,
            'print(resp.json())',
        ].join('\n');
    }

    const params = reqQ.length
        ? 'params={\n' +
          reqQ.map(([k, v]) => `    "${k}": "${v}",`).join('\n') +
          '\n}'
        : 'params={}';
    const url = base + path;
    return [
        'import requests',
        '',
        `${headers}`,
        `${params}`,
        `resp = requests.get("${url}", headers=headers, params=params)`,
        'print(resp.json())',
    ].join('\n');
};

const buildNode = (e: RefEndpoint, base: string): string => {
    const path = resolvePath(e);
    const reqQ = requiredQuery(e);
    const qs = reqQ.map(([k, v]) => `${k}=${v}`).join('&');
    const headers =
        'headers: {\n' +
        `    "Authorization": "Basic $CREDENTIALS",\n` +
        `    "api-consumer-key": "client-integration",\n` +
        '  }';

    if (isWrite(e)) {
        const url = base + path;
        return [
            `const res = await fetch("${url}", {`,
            `  method: "${e.method.toUpperCase()}",`,
            `  ${headers.replace(
                '}',
                '    "Content-Type": "application/json",\n  }',
            )},`,
            `  body: JSON.stringify(${oneLineBody(e.requestBodyExample)}),`,
            '});',
            'const data = await res.json();',
        ].join('\n');
    }

    const url = base + path + (qs ? `?${qs}` : '');
    return [
        `const res = await fetch("${url}", {`,
        `  ${headers},`,
        '});',
        'const data = await res.json();',
    ].join('\n');
};

/**
 * Build a copy-pasteable request snippet for an endpoint in the given language.
 * `base` is the environment's API base URL (e.g. `https://host/v1`).
 */
export const buildSnippet = (
    endpoint: RefEndpoint,
    lang: SnippetLang,
    base: string,
): string => {
    switch (lang) {
        case 'python':
            return buildPython(endpoint, base);
        case 'node':
            return buildNode(endpoint, base);
        case 'curl':
        default:
            return buildCurl(endpoint, base);
    }
};
